import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "./context/AuthContext";
import Playlists from "./Components/Playlists";
import Footer from "./Components/Footer";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Heading,
  Input,
  Text,
  VStack,
  useToast,
  Spinner,
  Center,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  HStack,
  Divider,
  useColorMode,
  IconButton,
  Tooltip
} from "@chakra-ui/react";
import { FaSun, FaMoon } from "react-icons/fa";

function Dashboard() {
  const { accessToken, isAuthenticated, isLoading } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const [userInfo, setUserInfo] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [generatedPlaylist, setGeneratedPlaylist] = useState(null);
  const [isLoadingPlaylist, setIsLoadingPlaylist] = useState(false);
  const [useLibraryOnly, setUseLibraryOnly] = useState(true);
  const [trackCount, setTrackCount] = useState(10);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const toast = useToast();

  useEffect(() => {
    if (!accessToken) return;

    axios
      .get("https://api.spotify.com/v1/me", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => setUserInfo(res.data))
      .catch((err) => {
        console.error("Failed to fetch profile", err);
        toast({
          title: "Error",
          description: "Failed to fetch user profile",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  }, [accessToken, toast]);

  const handlePromptSubmit = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt!",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsLoadingPlaylist(true);
    setGeneratedPlaylist(null);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/ai/recommendations`,
        { 
          prompt,
          useLibraryOnly,
          trackCount
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setGeneratedPlaylist(response.data);
      setRefreshTrigger(prev => prev + 1);
      toast({
        title: "Success",
        description: "Playlist generated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error generating playlist:", error);
      toast({
        title: "Error",
        description: "Failed to generate playlist. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingPlaylist(false);
    }
  };

  if (isLoading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="green.400" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxW="container.xl" py={16} minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
      <Box position="absolute" top={4} right={4}>
        <Tooltip label={colorMode === "light" ? "Dark mode" : "Light mode"}>
          <IconButton
            icon={colorMode === "light" ? <FaMoon /> : <FaSun />}
            onClick={toggleColorMode}
            variant="ghost"
            colorScheme="green"
            aria-label="Toggle color mode"
          />
        </Tooltip>
      </Box>
      <VStack spacing={12} align="stretch" w="100%" maxW="600px" flex="1">
        {/* Fixed Content Area */}
        <VStack spacing={8} align="stretch" flexShrink={0}>
          <VStack spacing={3} align="center" pt={8}>
            <Heading 
              size="2xl" 
              color={colorMode === "light" ? "gray.800" : "white"} 
              textAlign="center"
              bgGradient="linear(to-r, green.400, green.600)"
              bgClip="text"
              fontWeight="extrabold"
              letterSpacing="tight"
              lineHeight="1.2"
            >
              PlaylistGenie
            </Heading>
            <Text
              fontSize="lg"
              color={colorMode === "light" ? "gray.600" : "gray.300"}
              textAlign="center"
              maxW="400px"
              fontWeight="medium"
              letterSpacing="tight"
            >
              AI-powered playlists from your Spotify library
            </Text>
          </VStack>
          
          <Input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Tell us your mood, occasion, or vibe — like 'upbeat gym tracks'"
            size="lg"
            bg={colorMode === "light" ? "gray.100" : "gray.700"}
            color={colorMode === "light" ? "gray.800" : "white"}
            borderColor={colorMode === "light" ? "gray.300" : "gray.600"}
            _hover={{ borderColor: colorMode === "light" ? "gray.400" : "gray.500" }}
            _focus={{ borderColor: "green.400" }}
          />

          <Box
            bg={colorMode === "light" ? "gray.50" : "gray.700"}
            p={4}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={colorMode === "light" ? "gray.200" : "gray.600"}
          >
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} justify="space-between" align="center">
                <Box flex="1">
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={colorMode === "light" ? "gray.700" : "gray.200"}
                    mb={1}
                  >
                    Library Settings
                  </Text>
                  <Checkbox
                    isChecked={useLibraryOnly}
                    onChange={(e) => setUseLibraryOnly(e.target.checked)}
                    colorScheme="green"
                    color={colorMode === "light" ? "gray.700" : "gray.200"}
                    size="lg"
                  >
                    <Text fontSize="sm" color={colorMode === "light" ? "gray.600" : "gray.300"}>
                      Only use songs from my library
                    </Text>
                  </Checkbox>
                </Box>
                <Box flex="1">
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={colorMode === "light" ? "gray.700" : "gray.200"}
                    mb={1}
                  >
                    Playlist Size
                  </Text>
                  <HStack spacing={2} align="center">
                    <NumberInput
                      value={trackCount}
                      onChange={(value) => setTrackCount(Math.min(Math.max(1, parseInt(value) || 1), 50))}
                      min={1}
                      max={50}
                      size="sm"
                      w="100px"
                    >
                      <NumberInputField 
                        bg={colorMode === "light" ? "white" : "gray.600"} 
                        color={colorMode === "light" ? "gray.800" : "white"}
                        borderColor={colorMode === "light" ? "gray.300" : "gray.500"}
                        _hover={{ borderColor: colorMode === "light" ? "gray.400" : "gray.400" }}
                        _focus={{ borderColor: "green.400" }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper 
                          borderColor={colorMode === "light" ? "gray.300" : "gray.500"}
                          _hover={{ bg: colorMode === "light" ? "gray.100" : "gray.500" }}
                        />
                        <NumberDecrementStepper 
                          borderColor={colorMode === "light" ? "gray.300" : "gray.500"}
                          _hover={{ bg: colorMode === "light" ? "gray.100" : "gray.500" }}
                        />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="sm" color={colorMode === "light" ? "gray.600" : "gray.300"}>
                      tracks
                    </Text>
                  </HStack>
                </Box>
              </HStack>
            </VStack>
          </Box>

          <Button
            onClick={handlePromptSubmit}
            isLoading={isLoadingPlaylist}
            loadingText="Generating..."
            colorScheme="green"
            size="lg"
            w="100%"
            maxW="300px"
            mx="auto"
          >
            Generate Playlist
          </Button>
        </VStack>

        {/* Generated Playlist Display */}
        {generatedPlaylist && (
          <Box
            bg={colorMode === "light" ? "gray.100" : "gray.800"}
            borderRadius="lg"
            p={6}
            boxShadow="lg"
            flexShrink={0}
          >
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="green.400">
                ✨ {generatedPlaylist.playlist_name}
              </Heading>
              <Text color={colorMode === "light" ? "gray.600" : "gray.400"}>
                Your new playlist has been created! Here are the tracks:
              </Text>
              <VStack spacing={2} align="stretch">
                {generatedPlaylist.tracks.map((track, index) => (
                  <Box
                    key={index}
                    bg={colorMode === "light" ? "gray.200" : "gray.700"}
                    p={3}
                    borderRadius="md"
                  >
                    <Flex align="center" gap={4}>
                      <Text color={colorMode === "light" ? "gray.600" : "gray.400"} w="24px">
                        {index + 1}
                      </Text>
                      <Box>
                        <Text fontWeight="500" color={colorMode === "light" ? "gray.800" : "white"}>
                          {track.title}
                        </Text>
                        <Text color={colorMode === "light" ? "gray.600" : "gray.400"} fontSize="sm">
                          {track.artist}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </VStack>
          </Box>
        )}

        {/* Scrollable Playlists Section */}
        <Box flex="1" overflowY="auto" maxH="60vh">
          <VStack spacing={6} align="stretch">
            <Divider borderColor={colorMode === "light" ? "gray.300" : "gray.700"} />
            <Playlists accessToken={accessToken} refreshTrigger={refreshTrigger} />
          </VStack>
        </Box>
      </VStack>
      <Footer />
    </Container>
  );
}

export default Dashboard;
